import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n004m8mbnljin59sc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch (err: any) {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于丝瓜视频',
    description: '丝瓜视频品牌介绍、口碑数据与联系方式',
    url: 'https://sigua.video/about',
    isPartOf: { '@id': 'https://sigua.video/#website' },
    about: { '@id': 'https://sigua.video/#organization' },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
              关于<strong>丝瓜视频</strong>
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              聚合<strong>51视频</strong>、<strong>91视频</strong>等平台最新影视资源，致力于为用户提供
              高清流畅的免费在线追剧体验，让每一个热爱影视的人都能轻松享受精彩内容。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                  <p>
                    丝瓜视频诞生于对影视内容传播的热情。我们深知，在信息爆炸的时代，
                    找到优质、最新的影视资源并不容易。因此，我们聚合了<strong>51视频</strong>、<strong>91视频</strong>、<strong>糖心视频</strong>
                    等多平台的影视资源，为用户打造一个一站式的在线追剧平台。
                  </p>
                  <p>
                    我们的团队由一群热爱影视的年轻人组成。我们关注每一部新剧的上线动态，
                    确保在第一时间完成资源更新。无论是热门的国产剧、韩剧、美剧，
                    还是最新上映的电影大片，你都能在丝瓜视频找到。
                  </p>
                  <p>
                    从最初的几个人到如今服务数百万用户，丝瓜视频始终坚持
                    <strong>免费、高清、不缓冲</strong>的核心承诺。
                    我们相信，好的内容应该被更多人看到，好的观影体验不应该被付费门槛所阻碍。
                  </p>
                </div>
              </div>
              <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading text-xl text-foreground-950 mb-5 text-center">
                  品牌里程碑
                </h3>
                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="font-heading text-primary-500 text-sm">2022</span>
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-foreground-900 mb-1">平台创立</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">
                        丝瓜视频正式上线，以聚合51视频、91视频等平台资源为核心，开启免费追剧新时代。
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="font-heading text-accent-500 text-sm">2023</span>
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-foreground-900 mb-1">用户突破百万</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">
                        凭借高清流畅的观影体验和及时的更新速度，平台月活用户突破百万。
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="font-heading text-primary-500 text-sm">2024</span>
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-foreground-900 mb-1">全面升级</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">
                        网站全面改版升级，优化用户体验，新增分类导航和智能推荐功能。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                口碑与数据
              </h2>
              <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                用数据说话，用口碑证明
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-smile-line text-2xl text-primary-500"></i>
                </div>
                <p className="font-heading text-3xl text-primary-500 mb-1">500万+</p>
                <p className="text-foreground-500 text-sm">月活跃用户</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-movie-line text-2xl text-accent-500"></i>
                </div>
                <p className="font-heading text-3xl text-accent-500 mb-1">700+</p>
                <p className="text-foreground-500 text-sm">影视资源</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-thumb-up-line text-2xl text-primary-500"></i>
                </div>
                <p className="font-heading text-3xl text-primary-500 mb-1">98.5%</p>
                <p className="text-foreground-500 text-sm">用户好评率</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-speed-line text-2xl text-accent-500"></i>
                </div>
                <p className="font-heading text-3xl text-accent-500 mb-1">&lt;0.5s</p>
                <p className="text-foreground-500 text-sm">平均加载时间</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                有任何问题或建议？欢迎通过以下方式联系我们，或填写表单我们会尽快回复。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                <div className="w-11 h-11 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-foreground-500 text-xs">service@sigua.video</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                <div className="w-11 h-11 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">微信公众号</h3>
                <p className="text-foreground-500 text-xs">丝瓜视频官方</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                <div className="w-11 h-11 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-weibo-line text-xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">官方微博</h3>
                <p className="text-foreground-500 text-xs">@丝瓜视频官方</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="contact-form-about"
              className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
            >
              <h3 className="font-heading text-lg text-foreground-950 mb-5">发送消息</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  消息内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的消息内容（500字以内）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />

              {formStatus === 'error' && formError && (
                <div className="mb-4 p-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm">
                  <i className="ri-checkbox-circle-line mr-1.5"></i>
                  消息已发送成功！我们会尽快回复您，感谢您的关注。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>发送中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>发送消息
                  </>
                )}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}